import { zodResolver } from '@hookform/resolvers/zod'
import * as Sentry from '@sentry/react'
import { Info, Loader2Icon } from 'lucide-react'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { z } from 'zod'

import { toastContainerSchema } from '@/@types/schemas/toast-containers'
import { showError } from '@/components/toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import useTranslator from '@/hooks/translation'
import { translate } from '@/i18n/translate'
import { NetworkError } from '@/services/fintalk/api/types/errors/NetworkError'
import { createDashboard } from '@/services/fintalk/services/fintalk-backoffice/dashboards/create'
import { updateDashboard } from '@/services/fintalk/services/fintalk-backoffice/dashboards/update'

import { useDashboardEditDialog } from './hooks/use-dashboard-edit-dialog'
import { SupersetIdInfo } from './superset-id-info'

const createEditDashboardSchema = () =>
  z.object({
    name: z.string().min(1, translate('dashboards.edit.form.name.required')),
    supersetId: z
      .string()
      .min(1, translate('dashboards.edit.form.supersetId.required'))
      .uuid(translate('dashboards.edit.form.supersetId.invalidUuid')),
    defaultDashboard: z.boolean().default(false),
  })

type EditDashboardSchemaType = z.infer<
  ReturnType<typeof createEditDashboardSchema>
>

type DashboardEditDialogProps = {
  onSaved: () => void
}

const DashboardEditDialog = ({ onSaved }: DashboardEditDialogProps) => {
  const { getTranslation } = useTranslator()
  const { open, onOpenChange, data } = useDashboardEditDialog()
  const { botId } = useParams()
  const dashboard = data?.dashboard
  const isUpdate = Boolean(dashboard)

  if (!botId) {
    throw new Error('Missing botId')
  }

  const editDashboardSchema = useMemo(() => createEditDashboardSchema(), [])

  const form = useForm<EditDashboardSchemaType>({
    resolver: zodResolver(editDashboardSchema),
    defaultValues: dashboard
      ? {
          name: dashboard.name,
          supersetId: dashboard.external_dashboard_id,
          defaultDashboard: dashboard.bot_id === 'DEFAULT',
        }
      : {
          name: '',
          supersetId: '',
          defaultDashboard: false,
        },
  })

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = form

  const handleOnSubmit = async (formData: EditDashboardSchemaType) => {
    try {
      if (dashboard) {
        await updateDashboard({
          dashboardId: dashboard.dashboard_id,
          botId,
          name: formData.name,
          externalDashboardId: formData.supersetId,
          applyBotFilter: true,
          defaultDashboard: formData.defaultDashboard,
        })
      } else {
        await createDashboard({
          botId,
          name: formData.name,
          externalDashboardId: formData.supersetId,
          applyBotFilter: true,
          defaultDashboard: formData.defaultDashboard,
        })
      }
      reset()
      onOpenChange(false)
      onSaved()
    } catch (error) {
      if (error instanceof NetworkError) {
        showError({
          title: getTranslation('dashboards.edit.toast.networkError.title'),
          description: getTranslation(
            'dashboards.edit.toast.networkError.description',
          ),
          containerId: toastContainerSchema.Enum.BOT_LAYOUT,
        })
        return
      }

      const eventId = Sentry.captureException(error)
      showError({
        title: getTranslation('dashboards.edit.toast.unknownError.title'),
        description:
          getTranslation('dashboards.edit.toast.unknownError.description') +
          `\nEvent ID: ${eventId}`,
        containerId: toastContainerSchema.Enum.BOT_LAYOUT,
      })
    }
  }

  const handleCancel = () => {
    reset()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="px-8 pb-2 pt-6">
          <DialogTitle className="b text-lg font-bold text-foreground">
            {isUpdate
              ? getTranslation('dashboards.edit.title.update')
              : getTranslation('dashboards.edit.title.create')}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-8">
            <div className="space-y-6 px-8">
              {/* Dashboard Name */}
              <FormField
                control={form.control}
                name="name"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>
                      {getTranslation('dashboards.edit.form.name.label')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={getTranslation(
                          'dashboards.edit.form.name.placeholder',
                        )}
                        {...field}
                      />
                    </FormControl>
                    {fieldState.error ? (
                      <FormMessage />
                    ) : (
                      <FormDescription className="flex items-center gap-1 text-xs">
                        <Info className="h-3 w-3" />
                        <span>
                          {getTranslation('dashboards.edit.form.name.info')}
                        </span>
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              {/* ID Superset */}
              <FormField
                control={form.control}
                name="supersetId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      {getTranslation('dashboards.edit.form.supersetId.label')}
                      <SupersetIdInfo />
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={getTranslation(
                          'dashboards.edit.form.supersetId.placeholder',
                        )}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Global Dashboard */}
              <FormField
                control={form.control}
                name="defaultDashboard"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between gap-6">
                      <div className="space-y-1">
                        <FormLabel>
                          {getTranslation(
                            'dashboards.edit.form.defaultDashboard.label',
                          )}
                        </FormLabel>
                        <FormDescription>
                          {getTranslation(
                            'dashboards.edit.form.defaultDashboard.info',
                          )}
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="sm:justify-between">
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                {getTranslation('dashboards.edit.cancel')}
              </Button>
              <Button type="submit" variant="fintalk" disabled={isSubmitting}>
                {isUpdate
                  ? getTranslation('dashboards.edit.submit.label.update')
                  : getTranslation('dashboards.edit.submit.label.create')}
                {isSubmitting && <Loader2Icon className="animate-spin" />}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export { DashboardEditDialog }
